/**
 * Converts email to a stable positive integer using a simple hash
 * This creates a deterministic user ID from email for the API
 */
function emailToStablePositiveInt(email: string): number {
  const normalized = email.trim().toLowerCase();
  let hash = 0;
  for (let i = 0; i < normalized.length; i++) {
    const char = normalized.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  // Force positive and ensure non-zero
  const value = Math.abs(hash) || 1;
  return value;
}

/**
 * Creates a token from email in the format expected by the API: base64(userId:secret)
 */
export const makeTokenFromEmail = (email: string, secret = "x"): string => {
  const id = emailToStablePositiveInt(email);
  const plain = `${id}:${secret}`;
  return btoa(plain);
}

/**
 * Gets the logged-in user data from the cookie
 */
const getUserFromCookie = (): { email?: string; id?: string; username?: string } | null => {
  try {
    const cookieRow = document.cookie
      .split('; ')
      .find(row => row.startsWith('user='));
    // Use slice(1).join('=') to handle '=' in the cookie value (base64 padding)
    const cookieValue = cookieRow?.split('=').slice(1).join('=');
    if (cookieValue) {
      const decoded = decodeURIComponent(cookieValue);
      return JSON.parse(decoded);
    }
  } catch (e) {
    console.warn('Failed to get user from cookie:', e);
  }
  return null;
}

/**
 * Checks if a string looks like a valid email
 */
const isValidEmail = (str: string): boolean => {
  return str.includes('@') && str.includes('.');
}

/**
 * Checks if an identifier is a generic/placeholder value that shouldn't be used
 */
const isGenericIdentifier = (str: string): boolean => {
  const generic = ['user', 'guest', 'anonymous', 'unknown', ''];
  return generic.includes(str.toLowerCase().trim());
}

/**
 * Checks if a string is a valid unique identifier (email or unique username)
 */
const isValidUniqueIdentifier = (str: string): boolean => {
  if (!str || str.trim().length < 2) return false;
  if (isGenericIdentifier(str)) return false;
  return true;
}

/**
 * Gets the persistent user ID from localStorage (base64 encoded)
 * This survives cookie expiration and is set during login
 */
const getIdentifierFromLocalStorage = (): string | null => {
  try {
    const persistentId = localStorage.getItem('stokastic_user_id');
    if (persistentId) {
      const decoded = atob(persistentId);
      if (isValidUniqueIdentifier(decoded)) {
        return decoded;
      }
    }
  } catch (e) {
    // localStorage not available or invalid base64
  }
  return null;
}

/**
 * Gets the user's unique identifier from multiple sources with fallbacks
 * Priority: cookie.email -> cookie.id (decoded) -> cookie.username -> localStorage
 * Accepts emails or unique usernames, rejects generic values like "User"
 */
const getUniqueIdentifier = (): string | null => {
  const user = getUserFromCookie();

  // Check cookie sources first
  if (user) {
    // Check email field first (most reliable, always unique)
    if (user.email && isValidEmail(user.email)) {
      return user.email;
    }

    // Check id field - might be base64 encoded email or username
    if (user.id) {
      try {
        const decoded = atob(user.id);
        if (isValidUniqueIdentifier(decoded)) {
          return decoded;
        }
      } catch (e) {
        // id is not valid base64, ignore
      }
    }

    // Check username only if it's unique (not "User")
    if (user.username && isValidUniqueIdentifier(user.username)) {
      return user.username;
    }
  }

  // Final fallback: check localStorage for persistent user ID
  return getIdentifierFromLocalStorage();
}

/**
 * Gets the logged-in user's identifier (email or username)
 */
export const getUserEmail = (): string => {
  return getUniqueIdentifier() || '';
}

/**
 * Gets a valid token for the API. Uses unique identifier to generate a consistent user ID.
 * Returns null if user is not logged in.
 */
export const getAuthToken = (): string | null => {
  const identifier = getUniqueIdentifier();
  if (!identifier) {
    return null;
  }
  return makeTokenFromEmail(identifier);
}

/**
 * Extracts error message from API response
 */
const getErrorMessage = (errorResponse: any): string => {
    if (typeof errorResponse === 'string') return errorResponse;
    if (errorResponse?.message) return errorResponse.message;
    if (errorResponse?.Message) return errorResponse.Message;
    return 'An error occurred';
}

export const addCSVFile = async (payload: any) => {
    const token = getAuthToken();
    if (!token) throw new Error('User not logged in');

    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/userEntries/upload`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        },
        body: JSON.stringify({...payload, Token: token })
    });

    if (!res.ok) {
        const errorResponse = await res.json();
        throw new Error(getErrorMessage(errorResponse));
    }

    return res.json();
};

export const addLineupsToContest = async (payload: any) => {
    const token = getAuthToken();
    if (!token) throw new Error('User not logged in');

    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/userEntries`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        },
        body: JSON.stringify({ ...payload, Token: token })
    });

    if (!res.ok) {
        const errorResponse = await res.json();
        throw new Error(getErrorMessage(errorResponse));
    }

    return res.json();
};

export const removeLineups = async (payload: any) => {
    const token = getAuthToken();
    if (!token) throw new Error('User not logged in');

    const params = new URLSearchParams({
        Sport: payload?.Sport,
        Token: token
    });
    const contestIds = payload?.ContestIds;

    for (const id of contestIds) {
        params.append("ContestIds", id);
    };

    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/userEntries?${params}`, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        }
    });

    if (!res.ok) {
        const errorResponse = await res.json();
        throw new Error(getErrorMessage(errorResponse));
    };

    return res.json();
}